"use client";

import Link from "next/link";
import { useEffect, useState, type ComponentType } from "react";
import {
  ArrowRight,
  Bell,
  CalendarDays,
  Flame,
  Gift,
  Megaphone,
  Palette,
  PartyPopper,
  Rocket,
  Sparkles,
  Tag,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import {
  ANNOUNCEMENT_DISMISS_STORAGE_KEY,
  announcementSignature,
  isAnnouncementDismissed,
  isExternalAnnouncementHref,
  safeAnnouncementHref,
  shouldShowAnnouncement,
  type Announcement,
  type AnnouncementIconKey,
} from "@/lib/landing/announcement";
import { cn } from "@/lib/utils";

/**
 * Icon key → component. Statically mapped (not a dynamic lucide lookup) so the
 * landing bundle carries exactly these twelve glyphs and nothing else.
 */
export const ANNOUNCEMENT_ICONS: Record<AnnouncementIconKey, ComponentType<{ className?: string }>> =
  {
    sparkles: Sparkles,
    rocket: Rocket,
    megaphone: Megaphone,
    flame: Flame,
    zap: Zap,
    gift: Gift,
    party: PartyPopper,
    tag: Tag,
    wrench: Wrench,
    palette: Palette,
    calendar: CalendarDays,
    bell: Bell,
  };

/**
 * Full-width announcement strip above the marketing landing header.
 *
 * Content, icon, link, and CTA label are edited in Admin → Site Settings →
 * Announcement Bar, and the same panel controls who sees it (admins only, or
 * every visitor) so a launch is a toggle rather than a deploy.
 *
 * Deliberately NOT sticky: it sits above the sticky header and scrolls away, so
 * it costs no permanent vertical space and no sticky top-offset elsewhere on the
 * page has to shift to accommodate it.
 *
 * The signed-in subscriber home never renders this — that surface surfaces news
 * inline in its tool grid instead of an announcement band.
 */
export function LandingAnnouncementBar({ announcement }: { announcement: Announcement | null }) {
  const { isAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const signature = announcement ? announcementSignature(announcement) : null;

  // Read after mount: localStorage is unavailable during SSR, and the effect
  // re-runs whenever the content changes, which is what un-dismisses a NEW
  // announcement for visitors who dismissed the previous one.
  useEffect(() => {
    if (!announcement) return;
    try {
      setDismissed(
        isAnnouncementDismissed(
          window.localStorage.getItem(ANNOUNCEMENT_DISMISS_STORAGE_KEY),
          announcement,
        ),
      );
    } catch {
      // Private mode / blocked storage: treat as not dismissed.
      setDismissed(false);
    }
  }, [announcement, signature]);

  if (!announcement || dismissed || !shouldShowAnnouncement(announcement, { isAdmin })) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(ANNOUNCEMENT_DISMISS_STORAGE_KEY, signature ?? "");
    } catch {
      // Dismissal just won't persist across reloads.
    }
  };

  return (
    <AnnouncementBarSurface
      text={announcement.text}
      href={announcement.href}
      icon={announcement.icon}
      ctaLabel={announcement.ctaLabel}
      onDismiss={handleDismiss}
    />
  );
}

/**
 * Pixel-accurate, inert copy of the bar for the admin editor's live preview.
 *
 * Takes plain fields (not a saved {@link Announcement}) so it can render unsaved
 * draft content as you type. Both controls are decorative: the close button has
 * nothing to dismiss, and the link does not navigate — clicking a preview should
 * never bounce you out of the admin panel mid-edit.
 */
export function AnnouncementBarPreview({
  text,
  href,
  icon,
  ctaLabel,
  className,
}: {
  text: string;
  href: string | null;
  icon: AnnouncementIconKey | null;
  ctaLabel: string | null;
  className?: string;
}) {
  return (
    <AnnouncementBarSurface
      text={text}
      href={href}
      icon={icon}
      ctaLabel={ctaLabel}
      interactive={false}
      decorativeDismiss
      className={className}
    />
  );
}

/**
 * The bar itself, with no data loading, entitlement, or dismissal state.
 *
 * Colours come from the `--ann-*` tokens in globals.css, which are defined once
 * per palette and are the SAME in light and dark. That is deliberate: the strip
 * always reads as a dark notice band tinted to the active palette — warm
 * near-black with an orange accent on classic, navy with a blue accent on
 * cobalt. An earlier version inverted the `--lb-ink`/`--lb-bg` pair instead,
 * which turned the whole bar into a white slab on the dark theme.
 *
 * Not exported: Next flags non-serializable props on anything a `"use client"`
 * file exposes, so the two entry points above wrap it instead.
 */
function AnnouncementBarSurface({
  text,
  href,
  icon,
  ctaLabel,
  onDismiss,
  decorativeDismiss = false,
  interactive = true,
  className,
}: {
  text: string;
  href: string | null;
  icon: AnnouncementIconKey | null;
  ctaLabel: string | null;
  onDismiss?: () => void;
  decorativeDismiss?: boolean;
  /** False renders links as static text — used by the admin preview. */
  interactive?: boolean;
  className?: string;
}) {
  // Re-vetted at render: this component is also fed unsaved admin input, and the
  // value is world-readable, so it never trusts an href it was simply handed.
  const safeHref = safeAnnouncementHref(href);
  const external = isExternalAnnouncementHref(safeHref);
  const Icon = icon ? ANNOUNCEMENT_ICONS[icon] : null;
  // A CTA pill only makes sense with somewhere to go.
  const cta = safeHref && ctaLabel?.trim() ? ctaLabel.trim() : null;

  const linkProps = external
    ? ({ href: safeHref!, target: "_blank", rel: "noopener noreferrer" } as const)
    : null;

  const textClasses = "text-[13px] font-medium leading-snug tracking-[0.06em]";
  const arrow = (
    <ArrowRight
      aria-hidden
      className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
    />
  );

  const ctaClasses =
    "group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[hsl(var(--ann-accent))] px-3 py-1 text-[12px] font-semibold tracking-[0.02em] text-[hsl(var(--ann-accent-ink))] transition hover:brightness-110";
  // With a CTA pill the copy is static and the pill is the target; without one
  // the whole line is the link, so the arrow rides the text instead.
  const inlineLinkClasses = cn(
    textClasses,
    "group inline-flex items-center gap-2 underline-offset-4 transition hover:underline",
  );

  return (
    <div
      className={cn(
        "relative w-full border-b border-[hsl(var(--ann-line))] bg-[linear-gradient(180deg,hsl(var(--ann-surface-top)),hsl(var(--ann-surface)))] text-[hsl(var(--ann-ink))]",
        className,
      )}
    >
      <div className="mx-auto flex min-h-[2.75rem] flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-12 py-2 text-center sm:px-14">
        {Icon ? (
          <span
            aria-hidden
            className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-[hsl(var(--ann-accent)/0.16)] text-[hsl(var(--ann-accent))]"
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}

        {cta ? (
          <>
            <span className={textClasses}>{text}</span>
            {interactive && linkProps ? (
              <a {...linkProps} className={ctaClasses}>
                {cta}
                {arrow}
              </a>
            ) : interactive && safeHref ? (
              <Link href={safeHref} className={ctaClasses}>
                {cta}
                {arrow}
              </Link>
            ) : (
              <span className={ctaClasses}>
                {cta}
                {arrow}
              </span>
            )}
          </>
        ) : safeHref && interactive ? (
          linkProps ? (
            <a {...linkProps} className={inlineLinkClasses}>
              {text}
              {arrow}
            </a>
          ) : (
            <Link href={safeHref} className={inlineLinkClasses}>
              {text}
              {arrow}
            </Link>
          )
        ) : (
          <span className={cn(textClasses, "group inline-flex items-center gap-2")}>
            {text}
            {safeHref ? arrow : null}
          </span>
        )}
      </div>

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-[hsl(var(--ann-ink-soft))] transition hover:bg-[hsl(var(--ann-ink)/0.1)] hover:text-[hsl(var(--ann-ink))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ann-accent))] sm:right-3"
        >
          <X className="h-4 w-4" />
        </button>
      ) : decorativeDismiss ? (
        <span
          aria-hidden
          className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-[hsl(var(--ann-ink-soft))] sm:right-3"
        >
          <X className="h-4 w-4" />
        </span>
      ) : null}
    </div>
  );
}
